import { useShallow } from 'zustand/react/shallow';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Permissions as P,
  serializePermissions,
  parsePermissions,
  type Channel,
  type Invite,
  type Overwrite,
} from '@comms/shared';
import { useStore } from '../../store';
import { api } from '../../lib/api';
import { Button, ErrorText, Input, Modal, Tabs, cx } from '../ui';
import { CHANNEL_PERMISSION_KEYS, PERMISSION_GROUPS } from './permissionLabels';
import { WebhooksPanel } from './Webhooks';
import { has, usePermissions } from '../../hooks/permissions';

export function CreateChannelModal({
  guildId,
  parentId,
  type: initialType = 0,
  onClose,
}: {
  guildId: string;
  parentId?: string | null;
  type?: 0 | 2 | 4 | 15;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState<0 | 2 | 4 | 15>(initialType);
  const [isPrivate, setPrivate] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  async function create() {
    try {
      const c = await api<Channel>('POST', `/guilds/${guildId}/channels`, {
        name,
        type,
        ...(parentId && type !== 4 ? { parentId } : {}),
        ...(isPrivate
          ? {
              permissionOverwrites: [
                { id: guildId, type: 0, allow: '0', deny: serializePermissions(P.VIEW_CHANNEL) },
              ],
            }
          : {}),
      });
      onClose();
      if (c.type === 0 || c.type === 15) navigate(`/channels/${guildId}/${c.id}`);
    } catch (err) {
      setError(err);
    }
  }
  return (
    <Modal title={type === 4 ? 'Create Category' : 'Create Channel'} onClose={onClose}>
      <div className="space-y-4">
        {initialType !== 4 && (
          <div className="space-y-2">
            {[
              { t: 0 as const, label: '# Text', desc: 'Send messages, images, links and files' },
              {
                t: 2 as const,
                label: '🔊 Voice',
                desc: 'Hang out with voice, video and screen share',
              },
              {
                t: 15 as const,
                label: '💬 Forum',
                desc: 'Organized posts with their own threads and tags',
              },
            ].map((o) => (
              <label
                key={o.t}
                className={cx(
                  'flex cursor-pointer items-center gap-3 rounded p-3',
                  type === o.t ? 'bg-bg-active' : 'bg-bg-1 hover:bg-bg-hover',
                )}
              >
                <input type="radio" checked={type === o.t} onChange={() => setType(o.t)} />
                <span>
                  <span className="block font-medium text-text-0">{o.label}</span>
                  <span className="text-sm text-text-3">{o.desc}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        <Input
          label={type === 4 ? 'Category name' : 'Channel name'}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="new-channel"
          autoFocus
        />
        <label className="flex items-center gap-2 text-sm text-text-1">
          <input
            type="checkbox"
            checked={isPrivate}
            onChange={(e) => setPrivate(e.target.checked)}
          />{' '}
          Private — only selected roles and members can view
        </label>
        <ErrorText error={error} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!name.trim()} onClick={create}>
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function InviteModal({ channelId, onClose }: { channelId: string; onClose: () => void }) {
  const [invite, setInvite] = useState<Invite | null>(null);
  const [maxAge, setMaxAge] = useState(86400);
  const [maxUses, setMaxUses] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [copied, setCopied] = useState(false);
  const link = invite ? `${location.origin}/invite/${invite.code}` : '';
  async function create() {
    try {
      setInvite(await api<Invite>('POST', `/channels/${channelId}/invites`, { maxAge, maxUses }));
    } catch (err) {
      setError(err);
    }
  }
  return (
    <Modal title="Invite friends" onClose={onClose}>
      {invite ? (
        <div className="space-y-3">
          <p className="text-sm text-text-2">
            Share this link. People without an account can use it to register.
          </p>
          <div className="flex gap-2">
            <Input readOnly value={link} onFocus={(e) => e.target.select()} />
            <Button
              onClick={() => void navigator.clipboard.writeText(link).then(() => setCopied(true))}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="text-xs text-text-3">
            {invite.expiresAt
              ? `Expires ${new Date(invite.expiresAt).toLocaleString()}`
              : 'Never expires'}{' '}
            · {invite.maxUses ? `${invite.maxUses} uses` : 'unlimited uses'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <label className="block text-sm text-text-2">
            Expire after
            <select
              value={maxAge}
              onChange={(e) => setMaxAge(Number(e.target.value))}
              className="mt-1 w-full rounded bg-bg-0 p-2 text-text-0"
            >
              <option value={1800}>30 minutes</option>
              <option value={3600}>1 hour</option>
              <option value={86400}>1 day</option>
              <option value={604800}>7 days</option>
              <option value={0}>Never</option>
            </select>
          </label>
          <label className="block text-sm text-text-2">
            Max uses
            <select
              value={maxUses}
              onChange={(e) => setMaxUses(Number(e.target.value))}
              className="mt-1 w-full rounded bg-bg-0 p-2 text-text-0"
            >
              <option value={0}>No limit</option>
              {[1, 5, 10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <ErrorText error={error} />
          <Button onClick={create} className="w-full">
            Generate invite link
          </Button>
        </div>
      )}
    </Modal>
  );
}

type TriState = 'allow' | 'deny' | 'inherit';

export function OverwriteEditor({
  guildId,
  overwrites,
  onChange,
  keys,
}: {
  guildId: string;
  overwrites: Overwrite[];
  onChange: (o: Overwrite[]) => void;
  keys: Set<string>;
}) {
  const guild = useStore((s) => s.guilds[guildId]);
  const members = useStore(useShallow((s) => s.members[guildId] ?? {}));
  const [selected, setSelected] = useState<string>(overwrites[0]?.id ?? guildId);
  const current = overwrites.find((o) => o.id === selected);
  const roleTargets = guild?.roles.filter((r) => !overwrites.some((o) => o.id === r.id)) ?? [];

  function setState(flag: bigint, state: TriState) {
    const base = current ?? {
      id: selected,
      type: guild?.roles.some((r) => r.id === selected) ? (0 as const) : (1 as const),
      allow: '0',
      deny: '0',
    };
    let allow = parsePermissions(base.allow) & ~flag;
    let deny = parsePermissions(base.deny) & ~flag;
    if (state === 'allow') allow |= flag;
    if (state === 'deny') deny |= flag;
    const next = { ...base, allow: serializePermissions(allow), deny: serializePermissions(deny) };
    onChange([...overwrites.filter((o) => o.id !== selected), next]);
  }
  const stateOf = (flag: bigint): TriState =>
    current && (parsePermissions(current.allow) & flag) === flag
      ? 'allow'
      : current && (parsePermissions(current.deny) & flag) === flag
        ? 'deny'
        : 'inherit';
  const label = (id: string) =>
    guild?.roles.find((r) => r.id === id)?.name ?? members[id]?.user.displayName ?? id;

  return (
    <div className="flex gap-4">
      <div className="w-44 shrink-0 space-y-1">
        {[...new Set([guildId, ...overwrites.map((o) => o.id)])].map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setSelected(id)}
            className={cx(
              'block w-full truncate rounded px-2 py-1 text-left text-sm',
              selected === id ? 'bg-bg-active text-text-0' : 'text-text-2 hover:bg-bg-hover',
            )}
          >
            {label(id)}
          </button>
        ))}
        <select
          value=""
          onChange={(e) => {
            if (!e.target.value) return;
            const [kind, id] = e.target.value.split(':') as ['r' | 'm', string];
            onChange([...overwrites, { id, type: kind === 'r' ? 0 : 1, allow: '0', deny: '0' }]);
            setSelected(id);
          }}
          className="mt-2 w-full rounded bg-bg-0 p-1 text-sm text-text-1"
        >
          <option value="">+ Add role or member</option>
          {roleTargets.map((r) => (
            <option key={r.id} value={`r:${r.id}`}>
              Role: {r.name}
            </option>
          ))}
          {Object.values(members)
            .filter((m) => !overwrites.some((o) => o.id === m.user.id))
            .map((m) => (
              <option key={m.user.id} value={`m:${m.user.id}`}>
                Member: {m.user.displayName}
              </option>
            ))}
        </select>
      </div>
      <div className="max-h-[50vh] flex-1 overflow-y-auto">
        {PERMISSION_GROUPS.map((g) => {
          const items = g.items.filter((i) => keys.has(i.key));
          if (!items.length) return null;
          return (
            <div key={g.title} className="mb-4">
              <h4 className="mb-1 text-xs font-bold uppercase text-text-3">{g.title}</h4>
              {items.map((i) => {
                const flag = P[i.key];
                const st = stateOf(flag);
                return (
                  <div
                    key={i.key}
                    className="flex items-center justify-between border-b border-bg-3 py-2"
                  >
                    <span className="text-sm text-text-1">{i.label}</span>
                    <span className="flex overflow-hidden rounded border border-bg-3">
                      {(['deny', 'inherit', 'allow'] as const).map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setState(flag, s)}
                          title={s}
                          className={cx(
                            'px-2 py-0.5 text-sm',
                            st === s
                              ? s === 'allow'
                                ? 'bg-success text-white'
                                : s === 'deny'
                                  ? 'bg-danger text-white'
                                  : 'bg-bg-active text-text-0'
                              : 'text-text-3',
                          )}
                        >
                          {s === 'allow' ? '✓' : s === 'deny' ? '✕' : '/'}
                        </button>
                      ))}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ChannelSettingsModal({
  channelId,
  onClose,
}: {
  channelId: string;
  onClose: () => void;
}) {
  const channel = useStore((s) => s.channels[channelId]);
  const [tab, setTab] = useState<'overview' | 'permissions' | 'integrations'>('overview');
  const perms = usePermissions(channel?.guildId, channelId);
  const [name, setName] = useState(channel?.name ?? '');
  const [topic, setTopic] = useState(channel?.topic ?? '');
  const [slowmode, setSlowmode] = useState(channel?.rateLimitPerUser ?? 0);
  const [userLimit, setUserLimit] = useState(channel?.userLimit ?? 0);
  const [overwrites, setOverwrites] = useState<Overwrite[]>(channel?.permissionOverwrites ?? []);
  const [tags, setTags] = useState<TagDraft[]>(channel?.availableTags ?? []);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  if (!channel?.guildId) return null;
  const guildId = channel.guildId;

  async function save() {
    try {
      setError(null);
      await api('PATCH', `/channels/${channelId}`, {
        name,
        ...(channel!.type === 0 ? { topic: topic || null, rateLimitPerUser: slowmode } : {}),
        ...(channel!.type === 2 ? { userLimit } : {}),
        ...(channel!.type === 15 ? { topic: topic || null, availableTags: tags } : {}),
        permissionOverwrites: overwrites,
      });
      onClose();
    } catch (err) {
      setError(err);
    }
  }
  async function remove() {
    if (!confirm(`Delete #${channel!.name}? This cannot be undone.`)) return;
    await api('DELETE', `/channels/${channelId}`);
    onClose();
    navigate(`/channels/${guildId}`);
  }

  return (
    <Modal
      title={`Edit ${channel.type === 4 ? 'category' : `#${channel.name}`}`}
      onClose={onClose}
      wide
    >
      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'permissions', label: 'Permissions' },
          ...(channel.type === 0 && has(perms, P.MANAGE_WEBHOOKS)
            ? [{ id: 'integrations' as const, label: 'Integrations' }]
            : []),
        ]}
        value={tab}
        onChange={setTab}
      />
      <div className="mt-4">
        {tab === 'overview' ? (
          <div className="space-y-4">
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
            {channel.type === 15 && (
              <>
                <Input
                  label="Guidelines"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  maxLength={1024}
                />
                <TagEditor tags={tags} onChange={setTags} />
              </>
            )}
            {channel.type === 0 && (
              <>
                <Input
                  label="Topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  maxLength={1024}
                />
                <label className="block text-sm text-text-2">
                  Slowmode
                  <select
                    value={slowmode}
                    onChange={(e) => setSlowmode(Number(e.target.value))}
                    className="mt-1 w-full rounded bg-bg-0 p-2 text-text-0"
                  >
                    {[0, 5, 10, 30, 60, 300, 900, 3600].map((s) => (
                      <option key={s} value={s}>
                        {s === 0 ? 'Off' : s < 60 ? `${s}s` : `${s / 60}m`}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
            {channel.type === 2 && (
              <label className="block text-sm text-text-2">
                User limit: {userLimit === 0 ? 'unlimited' : userLimit}
                <input
                  type="range"
                  min={0}
                  max={99}
                  value={userLimit}
                  onChange={(e) => setUserLimit(Number(e.target.value))}
                  className="mt-1 w-full"
                />
              </label>
            )}
            <Button variant="danger" onClick={remove}>
              Delete {channel.type === 4 ? 'category' : 'channel'}
            </Button>
          </div>
        ) : tab === 'integrations' ? (
          <WebhooksPanel channelId={channelId} />
        ) : (
          <OverwriteEditor
            guildId={guildId}
            overwrites={overwrites}
            onChange={setOverwrites}
            keys={CHANNEL_PERMISSION_KEYS as Set<string>}
          />
        )}
      </div>
      <ErrorText error={error} />
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={save}>Save changes</Button>
      </div>
    </Modal>
  );
}

type TagDraft = { id?: string; name: string; emoji: string | null; moderated: boolean };

function TagEditor({ tags, onChange }: { tags: TagDraft[]; onChange: (t: TagDraft[]) => void }) {
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [moderated, setModerated] = useState(false);
  return (
    <div>
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-text-2">
        Post tags
      </span>
      <div className="mb-2 flex flex-wrap gap-1">
        {tags.length === 0 && <span className="text-sm text-text-3">No tags yet.</span>}
        {tags.map((t, i) => (
          <span
            key={t.id ?? `new-${i}`}
            className="flex items-center gap-1 rounded-full bg-bg-1 px-2 py-0.5 text-sm text-text-1"
          >
            {t.emoji} {t.name}
            {t.moderated && <span title="Moderators only">🛡</span>}
            <button
              type="button"
              title={`Remove ${t.name}`}
              onClick={() => onChange(tags.filter((_, j) => j !== i))}
              className="text-text-3 hover:text-danger"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      {tags.length < 20 && (
        <div className="flex items-center gap-2">
          <Input
            placeholder="Tag name"
            value={name}
            maxLength={20}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            placeholder="Emoji"
            value={emoji}
            className="w-20"
            onChange={(e) => setEmoji(e.target.value)}
          />
          <label className="flex shrink-0 items-center gap-1 text-xs text-text-2">
            <input
              type="checkbox"
              checked={moderated}
              onChange={(e) => setModerated(e.target.checked)}
            />
            Mods only
          </label>
          <Button
            variant="secondary"
            disabled={!name.trim()}
            onClick={() => {
              onChange([...tags, { name: name.trim(), emoji: emoji.trim() || null, moderated }]);
              setName('');
              setEmoji('');
              setModerated(false);
            }}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
}

export function CreateThreadModal({
  guildId,
  channelId,
  messageId,
  name: initialName = '',
  onClose,
}: {
  guildId: string;
  channelId: string;
  messageId?: string;
  name?: string;
  onClose: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [isPrivate, setPrivate] = useState(false);
  const [autoArchive, setAutoArchive] = useState(1440);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  async function create() {
    try {
      const res = await api<{ thread: Channel }>('POST', `/channels/${channelId}/threads`, {
        name: name.trim(),
        type: isPrivate ? 12 : 11,
        autoArchiveMinutes: autoArchive,
        ...(messageId ? { messageId } : {}),
      });
      onClose();
      navigate(`/channels/${guildId}/${res.thread.id}`);
    } catch (err) {
      setError(err);
    }
  }
  return (
    <Modal title="Create thread" onClose={onClose}>
      <div className="space-y-4">
        <Input
          label="Thread name"
          value={name}
          maxLength={100}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <label className="block text-sm text-text-2">
          Hide after inactivity
          <select
            value={autoArchive}
            onChange={(e) => setAutoArchive(Number(e.target.value))}
            className="mt-1 block w-full rounded bg-bg-0 px-3 py-2 text-text-0"
          >
            <option value={60}>1 hour</option>
            <option value={1440}>24 hours</option>
            <option value={4320}>3 days</option>
            <option value={10080}>1 week</option>
          </select>
        </label>
        {!messageId && (
          <label className="flex items-center gap-2 text-sm text-text-1">
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setPrivate(e.target.checked)}
            />{' '}
            Private — only people you invite or @mention can see it
          </label>
        )}
        <ErrorText error={error} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!name.trim()} onClick={create}>
            Create thread
          </Button>
        </div>
      </div>
    </Modal>
  );
}
